/**
 * The one place a request leaves the browser.
 *
 * Every error the backend returns is RFC 9457 Problem Details with a `requestId`.
 * This turns it into an {@link ApiError} that keeps the status, so a page can tell
 * "sign in again" (401) from "you may not" (403) from "this will never work"
 * (422), and keeps the request id, so a person can quote it.
 *
 * The session travels in `X-App-Session`, not `Authorization`: anything in front of
 * the application — a proxy asking for a password, an ingress — also claims
 * `Authorization`, and whichever writes last wins. In an earlier build that produced
 * a sign-in screen which came back after every successful sign-in.
 */

const SESSION_HEADER = 'X-App-Session';

let session: string | null = null;
const sessionRejectedHandlers = new Set<() => void>();

/** Set by the auth provider on sign-in, and cleared on sign-out. */
export function setSession(token: string | null): void {
  session = token;
}

/**
 * Called when the backend refuses the session this client sent: expired, or the
 * account was deactivated. Each provider signs out, so the next render sends the
 * person to sign in rather than showing every page's own 401.
 *
 * A set, not one slot: staff and applicant sessions have a provider each, and the
 * one that registered last would otherwise silently unhook the other.
 *
 * @returns the function that removes this handler
 */
export function onSessionRejected(handler: () => void): () => void {
  sessionRejectedHandlers.add(handler);
  return () => {
    sessionRejectedHandlers.delete(handler);
  };
}

export interface FieldProblem {
  field: string;
  message: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly title: string;
  readonly requestId: string | undefined;
  readonly fieldErrors: readonly FieldProblem[];
  /**
   * The backend's machine-readable reason, where one status covers several: a
   * stale version and a submitted application are both 409, and only one is
   * answered by reloading.
   */
  readonly code: string | undefined;
  /** The whole problem, for refusals that carry data, such as the latest draft. */
  readonly body: Readonly<Record<string, unknown>> | undefined;

  constructor(
    status: number,
    title: string,
    detail: string,
    requestId?: string,
    fieldErrors: readonly FieldProblem[] = [],
    code?: string,
    body?: Readonly<Record<string, unknown>>,
  ) {
    super(detail);
    this.name = 'ApiError';
    this.status = status;
    this.title = title;
    this.requestId = requestId;
    this.fieldErrors = fieldErrors;
    this.code = code;
    this.body = body;
  }
}

function isJson(response: Response): boolean {
  const type = response.headers.get('Content-Type') ?? '';
  return type.includes('application/json') || type.includes('+json');
}

async function toApiError(response: Response): Promise<ApiError> {
  const headerId = response.headers.get('X-Request-Id') ?? undefined;
  if (isJson(response)) {
    const body = (await response.json().catch(() => null)) as Record<string, unknown> | null;
    if (body) {
      return new ApiError(
        response.status,
        typeof body.title === 'string' ? body.title : 'Request failed',
        typeof body.detail === 'string'
          ? body.detail
          : `The request failed with status ${response.status}.`,
        typeof body.requestId === 'string' ? body.requestId : headerId,
        Array.isArray(body.errors) ? (body.errors as FieldProblem[]) : [],
        typeof body.code === 'string' ? body.code : undefined,
        body,
      );
    }
  }
  return new ApiError(
    response.status,
    'Request failed',
    `The request failed with status ${response.status}.`,
    headerId,
  );
}

/** Sends a request with the session, and turns a refusal into an {@link ApiError}. */
async function send(method: string, path: string, accept: string, payload?: unknown): Promise<Response> {
  const headers: Record<string, string> = { Accept: accept };
  const sentSession = session;
  if (sentSession) {
    headers[SESSION_HEADER] = sentSession;
  }
  const init: RequestInit = { method, headers };
  if (payload !== undefined) {
    init.headers = { ...init.headers, 'Content-Type': 'application/json' };
    init.body = JSON.stringify(payload);
  }

  const response = await fetch(path, init);

  if (!response.ok) {
    if (response.status === 401 && sentSession !== null) {
      for (const handler of sessionRejectedHandlers) handler();
    }
    throw await toApiError(response);
  }
  return response;
}

async function request<T>(method: string, path: string, payload?: unknown): Promise<T> {
  const response = await send(method, path, 'application/json', payload);
  if (response.status === 204) {
    return undefined as T;
  }
  // A 200 carrying HTML is the application's own index page, served because a
  // proxy did not forward the path. It used to surface as a JSON parse error
  // somewhere unrelated; this names it.
  if (!isJson(response)) {
    throw new ApiError(
      response.status,
      'Unexpected response',
      `${path} answered with ${response.headers.get('Content-Type') ?? 'no content type'} ` +
        'rather than data. The proxy may not be forwarding it to the API.',
      response.headers.get('X-Request-Id') ?? undefined,
    );
  }
  return (await response.json()) as T;
}

export interface DownloadedFile {
  blob: Blob;
  /** The name the server gave it, if it gave one. */
  fileName: string | null;
}

/**
 * A file the API returns rather than data. Fetched, not linked to, because the
 * session travels in a header a link cannot carry.
 */
async function download(path: string, accept: string): Promise<DownloadedFile> {
  // Problem Details too, so a refusal arrives as one rather than as a 406.
  const response = await send('GET', path, `${accept}, application/problem+json`);
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? null;
  return { blob: await response.blob(), fileName: name };
}

/**
 * Ends the session on the server, for signing out. Sent with `keepalive`, because
 * signing out is followed at once by leaving the page, which would otherwise cancel
 * it; and never reported, because the page is signed out either way.
 */
function end(path: string): void {
  if (!session) return;
  void fetch(path, { method: 'POST', headers: { [SESSION_HEADER]: session }, keepalive: true }).catch(
    () => undefined,
  );
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, payload?: unknown) => request<T>('POST', path, payload),
  put: <T>(path: string, payload?: unknown) => request<T>('PUT', path, payload),
  patch: <T>(path: string, payload?: unknown) => request<T>('PATCH', path, payload),
  delete: <T>(path: string) => request<T>('DELETE', path),
  download,
  end,
};
